import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react"
import { useGetUserPermissionsQuery } from "user/userService"
import { SelectOption } from "../components/PrevetMain"
import { PrevetSpeedGraphZoneType } from "../getSpeedChartOptionsHelpers"
import {
  InitialPrevetTabState,
  PrevetState,
  PrevetStateReducer,
  PrevetStateTabActions,
  PrevetTabState,
  PrevetTabs,
  UPDATE_TAB_STATE,
} from "./prevetStateReducer"

let persistedPrevetState: PrevetTabState | null = null

// Define the shape of the context value
interface PrevetTabStateContextType {
  getTabStateValue: (
    key: keyof PrevetState,
    index: number | undefined
  ) =>
    | SelectOption
    | PrevetSpeedGraphZoneType
    | string
    | boolean
    | number
    | number[]
    | undefined
  setTabStateValue: (
    key: keyof PrevetState,
    value:
      | SelectOption
      | PrevetSpeedGraphZoneType
      | string
      | boolean
      | number
      | undefined,
    index: number | undefined
  ) => void
  getSelectedIndex: () => number
  prevetTabStateDispatch: Dispatch<PrevetStateTabActions>
  reducerPrevetTabState: PrevetTabs
  getTabState: (index: number | undefined) => PrevetState | undefined
  isPortAlertAdmin: boolean
  showPortAlertModal: boolean
  setShowPortAlertModal: Dispatch<SetStateAction<boolean>>
  refetchTrigger: number
  triggerRefetch: () => void
}
// Create the Context with an initial value of undefined
const PrevetTabStateContext = createContext<
  PrevetTabStateContextType | undefined
>(undefined)

// Props for PrevetTabStateProvider
interface PrevetTabStateProviderProps {
  children: ReactNode
}

export const PrevetTabStateProvider: React.FC<PrevetTabStateProviderProps> = ({
  children,
}) => {
  const [reducerPrevetTabState, prevetTabStateDispatch] = useReducer(
    PrevetStateReducer,
    null,
    () =>
      persistedPrevetState ?? {
        tabStates: [...InitialPrevetTabState.tabStates],
        sequenceIndex: [...InitialPrevetTabState.sequenceIndex],
      }
  )

  useEffect(() => {
    if (reducerPrevetTabState) {
      persistedPrevetState = reducerPrevetTabState
    }
  }, [reducerPrevetTabState])

  const getSelectedPrevetTabState = useCallback(
    (id: number): PrevetState | undefined => {
      return reducerPrevetTabState && reducerPrevetTabState.tabStates[id]
    },
    [reducerPrevetTabState]
  )

  const getLastValue = (array: number[]): number | undefined => {
    if (array.length === 0) {
      console.log("The array is empty.")
      return undefined
    }

    return array[array.length - 1]
  }

  const getSelectedIndex = (): number => {
    if (reducerPrevetTabState)
      return getLastValue(reducerPrevetTabState.sequenceIndex) ?? -1
    return -1
  }

  const getTabStateValue = (
    key: keyof PrevetState,
    index: number | undefined
  ):
    | SelectOption
    | PrevetSpeedGraphZoneType
    | string
    | boolean
    | number
    | number[]
    | undefined => {
    if (reducerPrevetTabState) {
      let searchIndex =
        index ?? getLastValue(reducerPrevetTabState.sequenceIndex)
      const currentState = getSelectedPrevetTabState(searchIndex ?? 0)
      if (currentState) {
        return currentState[key]
      }
    }
  }

  // In prevetContext.ts
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0)

  const triggerRefetch = () => setRefetchTrigger(Date.now()) // Unique timestamp

  const getTabState = useCallback(
    (index: number | undefined): PrevetState | undefined => {
      if (reducerPrevetTabState) {
        const currentState = getSelectedPrevetTabState(
          index ?? getLastValue(reducerPrevetTabState.sequenceIndex) ?? 0
        )
        if (currentState) {
          return currentState
        }
      }
    },
    [reducerPrevetTabState]
  )

  const setTabStateValue = (
    key: keyof PrevetState,
    value:
      | SelectOption
      | PrevetSpeedGraphZoneType
      | string
      | boolean
      | number
      | undefined,
    index: number | undefined
  ) => {
    if (reducerPrevetTabState) {
      const getLastSequenceValue =
        index ?? getLastValue(reducerPrevetTabState.sequenceIndex) ?? 0
      const currentState = getSelectedPrevetTabState(getLastSequenceValue)

      if (currentState && key in currentState) {
        prevetTabStateDispatch({
          type: UPDATE_TAB_STATE,
          payload: {
            selectedIndex: getLastSequenceValue,
            key,
            value,
          },
        })
      }
    }
  }

  const {
    data: permissions,
    error: getPermissionsError,
    isLoading: getPermissionsLoading,
  } = useGetUserPermissionsQuery()

  const [showPortAlertModal, setShowPortAlertModal] = useState(false)

  return (
    <PrevetTabStateContext.Provider
      value={{
        reducerPrevetTabState,
        getTabState,
        getTabStateValue,
        setTabStateValue,
        getSelectedIndex,
        prevetTabStateDispatch,
        isPortAlertAdmin: !!permissions?.some((i) => i === "admin:portalert"),
        showPortAlertModal,
        setShowPortAlertModal,
        refetchTrigger,
        triggerRefetch
      }}
    >
      {children}
    </PrevetTabStateContext.Provider>
  )
}

// Custom hook to use the PrevetTabStateContext
export const usePrevetTabStateContext = (): PrevetTabStateContextType => {
  const context = useContext(PrevetTabStateContext)
  if (!context) {
    throw new Error(
      "usePrevetTabStateContext must be used within an PrevetTabStateProvider"
    )
  }
  return context
}
